/**
 * Generic mailbox username suggestions for the success-screen nudge.
 *
 * Internal model:
 *   - MAILBOX_SUGGESTION_IDS is the canonical, locale-independent ordering.
 *     IDs are taken from the English list ("info", "support", "admin", …) so
 *     they read meaningfully in code; they're stable identifiers for each
 *     ranked slot, not labels shown to users.
 *   - MAILBOX_SUGGESTION_VALUES maps an exact locale key (as the Control Panel
 *     reports it, e.g. "en", "pt-br", "es-es") to the username string at each
 *     ID. For non-English locales the value at a given ID is the locale's
 *     own term at that rank (e.g. id `info` → "contato" in pt-br).
 *     Locales not listed here fall back to DEFAULT_LOCALE_KEY.
 *
 * The usernames themselves are NOT translated — the Control Panel locale only
 * decides which set is used.
 */

export const MAILBOX_SUGGESTION_IDS = [
  "info",
  "support",
  "admin",
  "contact",
  "sales",
  "hello",
  "hr",
  "accounts",
  "marketing",
  "order",
] as const;

export type TMailboxSuggestionId = (typeof MAILBOX_SUGGESTION_IDS)[number];

/** First N IDs are tagged "popular" on every locale. */
export const MAILBOX_SUGGESTION_POPULAR_COUNT = 2;

/** Locale key used when the active locale has no entry in the value table. */
export const DEFAULT_LOCALE_KEY = "en";

/** Per-locale username at each ID. */
export const MAILBOX_SUGGESTION_VALUES: Readonly<
  Record<string, Readonly<Record<TMailboxSuggestionId, string>>>
> = {
  en: {
    info: "info",
    support: "support",
    admin: "admin",
    contact: "contact",
    sales: "sales",
    hello: "hello",
    hr: "hr",
    accounts: "accounts",
    marketing: "marketing",
    order: "order",
  },
  "pt-br": {
    info: "contato",
    support: "financeiro",
    admin: "suporte",
    contact: "comercial",
    sales: "atendimento",
    hello: "admin",
    hr: "vendas",
    accounts: "marketing",
    marketing: "administrativo",
    order: "compras",
  },
  "es-es": {
    info: "contacto",
    support: "info",
    admin: "ventas",
    contact: "admin",
    sales: "administracion",
    hello: "soporte",
    hr: "hola",
    accounts: "facturacion",
    marketing: "gerencia",
    order: "contabilidad",
  },
};
